import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { IconLogoMark } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

function Block({ className }: { className?: string }): ReactElement {
  return <div className={cn('bg-skeleton rounded-xs', className)} />
}

function SidebarBlock({ className }: { className?: string }): ReactElement {
  return <div className={cn('bg-skeleton-secondary rounded-xs', className)} />
}

interface ProgressBarProps {
  /** 0–1; Figma shows the bar at 100/160 of its width. */
  value?: number
}

/** Figma `progress-bar` (840:15624) — 160px wide, logo above a 6px track. */
export function ProgressBar({ value = 0.625 }: ProgressBarProps): ReactElement {
  return (
    <div
      data-node-id="840:15624"
      data-name="progress-bar"
      className="flex w-40 flex-col items-center gap-6"
    >
      <span className="text-progress-icon flex size-10 items-center justify-center">
        <IconLogoMark className="size-10" aria-hidden="true" />
      </span>
      <div
        className="bg-progress-base shadow-widget flex h-1.5 w-full items-start"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(value * 100)}
      >
        <div className="bg-progress-fill h-full" style={{ width: `${String(value * 100)}%` }} />
      </div>
    </div>
  )
}

interface SpinnerProps {
  size?: number
  className?: string
}

function Spinner({ size = 22, className }: SpinnerProps): ReactElement {
  return (
    <svg
      className={cn('animate-spin text-[#7a7a7a]', className)}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        pathLength="100"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeDasharray="72 28"
      />
    </svg>
  )
}

/** Spinner pinned near the top, matching the initial page-load spinner. */
export function PageSpinner({ className }: { className?: string }): ReactElement {
  const { t } = useTranslation()

  return (
    <div
      className={cn('-mt-6 flex w-full items-start justify-center', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <Spinner size={22} />
      <span className="sr-only">{t('state.loading')}</span>
    </div>
  )
}

/**
 * Full-page skeleton — Figma `1440px` (2206:280573) and the 900/600/400 frames.
 * The sidebar rail and its placeholder blocks only exist from 1200px up; below
 * that the header collapses to a menu button plus a title placeholder.
 */
export function SkeletonLoader(): ReactElement {
  const { t } = useTranslation()

  return (
    <div
      className="bg-canvas relative min-h-dvh w-full overflow-hidden"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {/* sidebar — 1200px and up */}
      <div className="bg-sidebar-surface border-sidebar-border absolute inset-y-0 left-0 hidden w-55.5 border-r lg:block">
        <SidebarBlock className="absolute top-4 left-3 size-7" />
        <SidebarBlock className="absolute top-4 left-45.5 size-7" />
        <SidebarBlock className="absolute top-16 left-6 h-7 w-30" />
        <SidebarBlock className="absolute top-25 left-6 h-7 w-25" />
        <SidebarBlock className="absolute top-34 left-6 h-7 w-20" />
        <SidebarBlock className="absolute top-47 left-6 h-3 w-20" />
        <SidebarBlock className="absolute top-52 left-6 h-7 w-30" />
        <SidebarBlock className="absolute top-65 left-6 h-3 w-20" />
        <SidebarBlock className="absolute top-70 left-6 h-7 w-30" />
      </div>

      {/* header placeholders — menu button + title below 1200px, offset by the rail above */}
      <Block className="absolute top-6 left-6 size-7 lg:hidden" />
      <Block className="absolute top-6 left-15 h-7 w-21 lg:hidden" />
      <Block className="absolute top-6 left-59.5 hidden h-7 w-30 lg:block" />

      <Block className="absolute top-18 left-6 h-7 w-62.5 lg:left-59.5" />

      <div className="absolute top-6 right-6 flex items-center gap-2">
        <Block className="size-7" />
        <Block className="size-7" />
        <Block className="size-7" />
      </div>

      <div className="absolute top-100 left-1/2 -translate-x-1/2 lg:left-[calc(50%+111px)]">
        <ProgressBar />
      </div>

      <span className="sr-only">{t('state.loading')}</span>
    </div>
  )
}
